
import { map as mapRxjs } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { each } from 'lodash';

import { settings } from '../../app.settings';
import { Hook } from '../../shared/models/hook';
import { HookHeader} from '../../shared/models/hook-header';
import { HookHmac } from '../../shared/models/hook-hmac';
import { HookBasic } from '../../shared/models/hook-basic';
import { Token } from '../../shared/models/token';
import { BaseService } from '~common/app/shared/services/base.service';
import { ApiErrorInterceptorConfig } from 'app/shared/services/api-error.interceptor';
import { AuthService } from 'app/auth/services/auth.service';
import { Router } from '@angular/router';
import { dateRange } from '~common/app/shared/utils/api';
import { NaiveParameterCodec } from '~common/app/shared/utils/encoding';

const API_PATH = settings.api.url + '/programs/me/events';
const API_PATH_FETCH = settings.api.url + '/events';
const API_PATH_TOKENS = settings.api.url + '/programs/me/api-keys';
const API_PATH_DOCS = settings.api.url + '/api/versions';
const API_PATH_INTEGRATION = settings.api.url + '/programs/me/';

@Injectable()
export class TechnicalService  extends BaseService {

  constructor(
    private router: Router,
    private authService: AuthService,
    private http: HttpClient
  ) {
    super();
  }

  // v2 API-keys methods — hit the perks endpoint (authz-backed, no legacy auth params)
  private readonly API_PATH_TOKENS_V2 = settings.api.urlPerks + '/programs/me/api-keys';
  private readonly API_PATH_AUTHZ_TOKENS = settings.api.urlAuthz + '/tokens';

  goToApiDoc(role, version = null) {
    role = role.replace('_', '-');
    role = role.toLowerCase();
    this.router.navigate([ '/developers/docs', role, version || '' ]);
  }

  fetchAPIDocs(params: any = {}) {
    return this.http.get(API_PATH_DOCS, {
      params: params
    });
  }

  fetchTokens(params: any = {}) {
    let apiParams = new HttpParams({ encoder: new NaiveParameterCodec() });

    each(params, (value: any, key) => {
      if (key === 'filters') {
        if (value.term) {
          apiParams = apiParams.append('keyword', value.term );
        }
      } else if (value || value === 0) {
        apiParams = apiParams.append(key, value);
      }
    });

    return this.http.get(API_PATH_TOKENS, {
      params: apiParams
    }).pipe(mapRxjs((response: any) => {
      return this.hydrate(Token, response);
    }));
  }

  getToken(id) {
    const params: any = {
      key: this.authService.getSecurityToken()
    };

    params.interceptorConfig = <ApiErrorInterceptorConfig> <unknown>{
      skipErrorStatuses: [401, 403]
    };

    return this.http.get(API_PATH_TOKENS + '/' + id, {
      params: params
    }).pipe(mapRxjs(response => {
      return this.hydrate(Token, response);
    }));
  }

  generateToken(data) {
    const params: any = {
      key: this.authService.getSecurityToken()
    };

    data.interceptorConfig = <ApiErrorInterceptorConfig> <unknown>{
      skipErrorStatuses: [401, 403]
    };

    return this.http.post(API_PATH_TOKENS, data, {
      params: params
    });
  }

  revokeToken(id) {
    const params: any = {
      key: this.authService.getSecurityToken()
    };

    const data = {
      interceptorConfig: <ApiErrorInterceptorConfig> <unknown>{
        skipErrorStatuses: [401, 403]
      }
    };

    return this.http.post(API_PATH_TOKENS + '/' + id + '/revoke', data, {
      params: params
    });
  }

  fetchEvents(params = {}) {
    let apiParams = new HttpParams({ encoder: new NaiveParameterCodec() });

    each(params, (value: any, key) => {
      if (key === 'filters') {
        if (value.term) {
          apiParams = apiParams.append('keyword', value.term );
        }
        if (value.type.length > 0) {
          apiParams = apiParams.append('callback__type', value.type );
        }
        if (value.status.length > 0) {
          apiParams = apiParams.append('status', value.status );
        }

        if (value.start_date || value.end_date) {
          apiParams = apiParams.append('created_at', dateRange(value.start_date, value.end_date));
        }
      } else if (value || value === 0) {
        apiParams = apiParams.append(key, value);
      }
    });

    return this.http.get(API_PATH_FETCH, {
      params: apiParams
    }).pipe(mapRxjs((response: any) => {
      return response;
    }));
  }

  getHooks() {
    return this.http.get(API_PATH).pipe(mapRxjs(response => {
      return this.hydrate(Hook, response);
    }));
  }

  getHmac() {
    return this.http.get(API_PATH + '/hmac', {
      params: this.needsDoubleAuth()
    }).pipe(mapRxjs(response => {
      return this.hydrate(HookHmac, response);
    }));
  }

  getHeader() {
    return this.http.get(API_PATH + '/header', {
      params: this.needsDoubleAuth()
    }).pipe(mapRxjs(response => {
      return this.hydrate(HookHeader, response);
    }));
  }

  getBasic() {
    return this.http.get(API_PATH + '/basic-auth', {
      params: this.needsDoubleAuth()
    }).pipe(mapRxjs(response => {
      return this.hydrate(HookBasic, response);
    }));
  }

  testHook(event_type: string, url: string) {
    return this.http.post(settings.api.url + '/events/programs/me/send', { event_type, url });
  }

  updateHook(data: any) {
    return this.http.put(API_PATH, data);
  }

  updateHeader(data: any) {
    return this.http.put(API_PATH + '/header', data, {
      params: this.needsDoubleAuth()
    });
  }

  updateHmac(data: any) {
    return this.http.put(API_PATH + '/hmac', data, {
      params: this.needsDoubleAuth()
    });
  }

  updateBasic(data: any) {
    return this.http.put(API_PATH + '/basic-auth', data, {
      params: this.needsDoubleAuth()
    });
  }

  deleteHeader() {
    return this.http.delete(API_PATH + '/header');
  }

  deleteHmac() {
    return this.http.delete(API_PATH + '/hmac');
  }

  deleteBasic() {
    return this.http.delete(API_PATH + '/basic-auth');
  }

  // Integration

  getIntegration(company: string) {
    return this.http.get(API_PATH_INTEGRATION + company, {
      params: this.needsDoubleAuth()
    });
  }

  updateIntegration(company: string, data: any) {
    return this.http.put(API_PATH_INTEGRATION + company, data, {
      params: this.needsDoubleAuth()
    });
  }

  fetchApiKeysV2(params: any = {}) {
    let apiParams = new HttpParams({ encoder: new NaiveParameterCodec() });

    each(params, (value: any, key) => {
      if (key === 'filters') {
        if (value.term) {
          apiParams = apiParams.append('keyword', value.term);
        }
      } else if (value || value === 0) {
        apiParams = apiParams.append(key, value);
      }
    });

    return this.http.get(this.API_PATH_TOKENS_V2, { params: apiParams });
  }

  generateApiKeyV2(data: any) {
    return this.http.post(this.API_PATH_TOKENS_V2, data);
  }

  revokeApiKeyV2(clientId: string) {
    return this.http.delete(this.API_PATH_TOKENS_V2 + '/' + clientId);
  }

  issueJwt(clientId: string, secret: string) {
    return this.http.post(this.API_PATH_AUTHZ_TOKENS, { id: clientId, secret });
  }

  private needsDoubleAuth() {
    return {};

    // const params: any = {
    //   key: this.authService.getSecurityToken()
    // };

    // params.interceptorConfig = <ApiErrorInterceptorConfig> <unknown>{
    //   skipErrorStatuses: [401, 403]
    // };

    // return params;
  }
}
